import * as React from 'react';
import { Box, Typography } from '@material-ui/core';
import { useStyles, cx } from '../../../custom-forms';
import { useAppSelector } from '../../../store/configureStore';
import { VehicleHelper } from '../../../helpers/VehicleHelper';
import { CalculationTotals } from '../types/alternative-calculation.types';
import { IReferential } from '../../../interfaces/IReferential';
import { useTranslate } from '../../../hooks/useTranslate';
import { displayCurrency, displayPercent } from '../utils/calculation.utils';

export interface AlternativeCalculationVehicleBandProps {
  totals: CalculationTotals;
  maintenancePackage: IReferential | null;
  currency: IReferential | null;
}

const AlternativeCalculationVehicleBand: React.FC<AlternativeCalculationVehicleBandProps> = ({
  totals,
  maintenancePackage,
  currency
}) => {
  const c = useStyles();
  const t = useTranslate();
  const caseSettingsState = useAppSelector((s) => s.caseSettings);
  const vehicleState = useAppSelector((s) => s.vehicle);
  const appState = useAppSelector((s) => s.app);
  const caseData = caseSettingsState?.case ?? null;
  const vehicle = vehicleState?.vehicle ?? null;
  const language = appState?.language ?? '';
  const country = appState?.appUser?.organization?.country ?? null;
  const currencyName = currency?.displayName ?? '';

  const infoFields: Array<[string, string]> = [
    ...(maintenancePackage
      ? ([
          [t('CALCULATION_DETAILS_PACKAGE_TYPE', 'Tip Pachet'), maintenancePackage.displayName]
        ] as Array<[string, string]>)
      : []),
    [t('CALCULATION_DETAILS_UMC', 'UMC'), VehicleHelper.GetUMC(caseData, vehicle)],
    [
      t('CALCULATION_DETAILS_VEHICAL_FIRST_REGISTRATION_DATE', 'Data primei inmatriculari'),
      VehicleHelper.GetFirstRegistrationDate(caseData, vehicle, language)
    ],
    [
      t('CALCULATION_DETAILS_VEHICLE_MANUFACTURE_DATE', 'Data fabricatie'),
      VehicleHelper.GetMakeDate(caseData, vehicle, language)
    ],
    [
      t('CALCULATION_DETAILS_VEHICAL_NUMBER_OF_KM', 'Numar kilometri'),
      String(VehicleHelper.GetKmNr(caseData, vehicle))
    ]
  ];

  const summaryRows: Array<{
    label: string;
    original: string;
    alternative: string;
    saving: string;
    positive: boolean;
  }> = [
    {
      label: t('ALT_CALC_ROW_CALC', 'Calculație'),
      original: displayCurrency(totals.originalAmountWithoutTVA, country, currencyName),
      alternative: displayCurrency(totals.alternativeAmountWithoutTVA, country, currencyName),
      saving: displayPercent(totals.totalSavingPct, country),
      positive: totals.totalSavingPct > 0
    },
    {
      label: t('ALT_CALC_ROW_PARTS', 'Piese'),
      original: displayCurrency(totals.originalPartsAmount, country, currencyName),
      alternative: displayCurrency(totals.alternativePartsAmount, country, currencyName),
      saving: displayPercent(totals.partsSavingPct, country),
      positive: totals.partsSavingPct > 0
    },
    {
      label: t('ALT_CALC_ROW_SMALL_PARTS', 'Piese mici'),
      original: displayCurrency(totals.originalSmallPartsAmount, country, currencyName),
      alternative: displayCurrency(totals.alternativeSmallPartsAmount, country, currencyName),
      saving: displayPercent(totals.smallPartsSavingPct, country),
      positive: totals.smallPartsSavingPct > 0
    }
  ];

  return (
    <Box className={cx(c.card, c.row, c.wrap, c.gap26, c.sectionPad)}>
      <Box className={cx(c.row, c.gap13, c.minW0)}>
        <Typography variant="body1" className={cx(c.outlinedTag, c.extrabold, c.upper, c.nowrap)}>
          {VehicleHelper.GetPlateNumber(caseData, vehicle)}
        </Typography>
        <Box>
          <Typography variant="h6" className={c.bold}>
            {VehicleHelper.GetBrandModel(caseData, vehicle)}
          </Typography>
          <Typography variant="caption" className={cx(c.mono, c.faint)}>
            {VehicleHelper.GetVIN(caseData, vehicle)}
          </Typography>
        </Box>
      </Box>

      <Box className={c.autoGrid4}>
        {infoFields.map(([label, value]) => (
          <React.Fragment key={label}>
            <Typography variant="caption" className={cx(c.upper, c.faint, c.nowrap)}>
              {label}
            </Typography>
            <Typography variant="body2" className={cx(c.semibold, c.nowrap)}>
              {value}
            </Typography>
          </React.Fragment>
        ))}
      </Box>

      <Box className={cx(c.summaryGrid, c.pushRight, c.tabular)}>
        <span />
        <Typography variant="caption" className={cx(c.wideUpper, c.faint, c.right)}>
          {t('ALT_CALC_ORIGINAL', 'Originală')}
        </Typography>
        <Typography variant="caption" className={cx(c.wideUpper, c.faint, c.right)}>
          {t('ALT_CALC_ALTERNATIVE', 'Alternativă')}
        </Typography>
        <Typography variant="caption" className={cx(c.wideUpper, c.faint, c.right)}>
          {t('ALT_CALC_SAVING', 'Economisire')}
        </Typography>

        {summaryRows.map((r) => (
          <React.Fragment key={r.label}>
            <Typography variant="body2" className={c.muted}>
              {r.label}
            </Typography>
            <Typography variant="body2" className={cx(c.semibold, c.right)}>
              {r.original}
            </Typography>
            <Typography variant="body2" className={cx(c.semibold, c.right, c.primaryText)}>
              {r.alternative}
            </Typography>
            <Typography
              variant="body2"
              className={cx(c.extrabold, c.right, c.pill, r.positive && c.pillSuccess)}
            >
              {r.saving}
            </Typography>
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

export default AlternativeCalculationVehicleBand;
